// React & Hooks
import React, { useState } from "react";
// API client
import api from "../../lib/api";
// Theme hook
import useTheme from "../../hooks/useTheme";
// i18n
import { useTranslation } from "react-i18next";

const AddSaleModal = ({ isOpen, onClose, onSaved, selectedDate }) => {
  // States
  const [productName, setProductName] = useState("");
  const [barrels, setBarrels] = useState([""]);
  const [busy, setBusy] = useState(false);

  // Hooks
  const { theme } = useTheme();
  const { t } = useTranslation();

  // Base button styles
  const baseBtn =
    theme === "dark"
      ? "inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold shadow bg-white text-black hover:bg-black hover:text-white"
      : "inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold shadow bg-black text-white hover:bg-white hover:text-black";

  // If modal is closed, return null
  if (!isOpen) return null;

  // Handle barrel add
  const addBarrel = () => setBarrels([...barrels, ""]);

  // Handle barrel update
  const updateBarrel = (i, v) => {
    const list = [...barrels];
    list[i] = v;
    setBarrels(list);
  };

  // Handle barrel remove
  const removeBarrel = (i) => {
    if (barrels.length === 1) return;
    const list = [...barrels];
    list.splice(i, 1);
    setBarrels(list);
  };

  // Calculate total sent weight
  const total = barrels.reduce((s, b) => s + (parseFloat(b) || 0), 0);

  // Save sale
  const handleSave = async () => {
    if (!productName || total <= 0) return;
    setBusy(true);
    try {
      await api.post("/sales", {
        productName,
        date: selectedDate,
        sentWeight: total,
        returnWeight: 0,
        barrels: barrels.map((b) => parseFloat(b) || 0), // Save tambos
      });
      onSaved();
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setBusy(false);
    }
  };

  // Render modal
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center">
      <div className="bg-white dark:bg-gray-900 p-6 rounded-lg shadow-lg w-96">
        {/* Header */}
        <h2 className="text-lg font-bold mb-4">{t("addSale")}</h2>

        {/* Product name input */}
        <input
          type="text"
          placeholder={t("productName")}
          className="w-full mb-2 p-2 border rounded 
                     bg-white text-black 
                     dark:bg-gray-900 dark:text-white dark:border-gray-600"
          value={productName}
          onChange={(e) => setProductName(e.target.value)}
        />

        {/* Barrel weights inputs */}
        {barrels.map((w, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <input
              type="number"
              placeholder={`${t("barrel")} ${i + 1} (kg)`}
              className="flex-1 p-2 border rounded 
                         bg-white text-black 
                         dark:bg-gray-900 dark:text-white dark:border-gray-600"
              value={w}
              onChange={(e) => updateBarrel(i, e.target.value)}
            />
            {barrels.length > 1 && (
              <button
                type="button"
                className="px-2 text-red-600 font-bold"
                onClick={() => removeBarrel(i)}
              >
                ✕
              </button>
            )}
          </div>
        ))}

        {/* Add barrel button */}
        <button
          type="button"
          className="text-sm text-blue-600 mb-4"
          onClick={addBarrel}
        >
          + {t("addBarrel")}
        </button>

        {/* Results */}
        <div className="mt-4 p-3 border rounded bg-gray-100 dark:bg-gray-800 space-y-1">
          {barrels.map((b, i) => (
            <p key={i}>
              <strong>{t("barrel")} {i + 1}:</strong> {(parseFloat(b) || 0).toFixed(2)} KG
            </p>
          ))}
          <p>
            <strong>{t("sentWeight")}:</strong> {total.toFixed(2)} KG
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex justify-end gap-2 mt-6">
          <button className={baseBtn} onClick={onClose}>{t("cancel")}</button>
          <button className={baseBtn} onClick={handleSave} disabled={busy}>
            {busy ? t("saving") : t("save")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddSaleModal;